import { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { IPhoneRuleConfig } from "../../../setup/api/services/phoneRuleConfigs/models";
import { getCountryDisplayName } from "../../../setup/util/phoneRuleConfig.helpers";
import CountryFlag from "./CountryFlag";
import CountrySheet from "./CountrySheet";
import { useDismissable } from "./useDismissable";
import styles from "./styles.module.scss";

interface Props {
  countries: IPhoneRuleConfig[];
  value?: IPhoneRuleConfig;
  onChange: (country: IPhoneRuleConfig) => void;
  placeholder?: string;
  errorMessage?: string;
  disabled?: boolean;
}

/** A full-width country field styled like TextInput, sharing the phone picker's sheet. */
const CountrySelect = ({
  countries,
  value,
  onChange,
  placeholder,
  errorMessage,
  disabled,
}: Props) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useDismissable(containerRef, isOpen, close);

  return (
    <div className={styles.outer} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        className={classNames(styles.field, styles.selectField, {
          [styles.fieldFocused]: isOpen,
          [styles.fieldError]: !!errorMessage,
        })}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}>
        {value ? (
          <span className={styles.selectValue}>
            <CountryFlag
              emoji={value.countryFlag}
              countryName={value.countryName}
              size={22}
            />
            <span>{getCountryDisplayName(value, i18n.language)}</span>
          </span>
        ) : (
          <span className={styles.selectPlaceholder}>{placeholder}</span>
        )}
        <img
          className={classNames(styles.chevron, {
            [styles.chevronOpen]: isOpen,
          })}
          src="/assets/images/svg/downArrow.svg"
          alt=""
        />
      </button>

      {isOpen && (
        <CountrySheet
          className={styles.sheetFullWidth}
          countries={countries}
          selectedCountryCode={value?.countryCode}
          hideDialCode
          onSelect={(country) => {
            onChange(country);
            close();
          }}
        />
      )}

      {errorMessage && (
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{errorMessage}</p>
        </div>
      )}
    </div>
  );
};

export default CountrySelect;
